import i18n from '../i18n'

// Adjustment labels (#714): read when the breakdown is built, i.e. at render.
const adj = (key: 'metallic' | 'metallicDescription' | 'towingHitch' | 'towingHitchDescription'
  | 'service' | 'serviceDescription' | 'oppCost' | 'oppCostDescription' | 'lowMileage' | 'highMileage'
  | 'lowMileageDescription' | 'highMileageDescription' | 'manualCorrection' | 'manualCorrectionDescription'): string =>
  i18n.t(`adjustments.${key}` as const, { ns: 'kampanjer' })

// Helper function to safely parse numeric values
export const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

// Format currency in Norwegian locale
export const formatCurrency = (value: number | string | null | undefined): string => {
  const num = toNumber(value)
  return num.toLocaleString('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kr'
}

// Format percentage (value already in percent, e.g. 45.5 for 45.5%)
export const formatPercent = (value: number | string | null | undefined, decimals: number = 2): string => {
  const num = toNumber(value)
  return num.toLocaleString('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + ' %'
}

// Format integer with thousand separator
export const formatInteger = (value: number | string | null | undefined): string => {
  const num = toNumber(value)
  return num.toLocaleString('nb-NO', { maximumFractionDigits: 0 })
}

// Format decimal number with fixed precision
export const formatDecimal = (value: number | string | null | undefined, decimals: number = 1): string => {
  const num = toNumber(value)
  return num.toLocaleString('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

// Calculate total adjustments
export const calculateTotalAdjustments = (result: any): number => {
  return (
    toNumber(result.AdjustMetallic) +
    toNumber(result.AdjustService) +
    toNumber(result.AdjustTowingHitch) +
    toNumber(result.AdjustOppCost) +
    toNumber(result.AdjustMileage) +
    toNumber(result.AdjustHighMileage) +
    toNumber(result.AdjustManualCorrection)
  )
}

// Get non-zero adjustments with labels
export interface AdjustmentItem {
  label: string
  value: number
  description: string
  type: 'increase' | 'decrease'
}

export const getAdjustmentBreakdown = (result: any): AdjustmentItem[] => {
  const adjustments: AdjustmentItem[] = []

  if (toNumber(result.AdjustMetallic) !== 0) {
    adjustments.push({
      label: adj('metallic'),
      value: toNumber(result.AdjustMetallic),
      description: adj('metallicDescription'),
      type: toNumber(result.AdjustMetallic) > 0 ? 'increase' : 'decrease',
    })
  }

  if (toNumber(result.AdjustTowingHitch) !== 0) {
    adjustments.push({
      label: adj('towingHitch'),
      value: toNumber(result.AdjustTowingHitch),
      description: adj('towingHitchDescription'),
      type: toNumber(result.AdjustTowingHitch) > 0 ? 'increase' : 'decrease',
    })
  }

  if (toNumber(result.AdjustService) !== 0) {
    adjustments.push({
      label: adj('service'),
      value: toNumber(result.AdjustService),
      description: adj('serviceDescription'),
      type: toNumber(result.AdjustService) > 0 ? 'increase' : 'decrease',
    })
  }

  if (toNumber(result.AdjustOppCost) !== 0) {
    adjustments.push({
      label: adj('oppCost'),
      value: toNumber(result.AdjustOppCost),
      description: adj('oppCostDescription'),
      type: toNumber(result.AdjustOppCost) > 0 ? 'increase' : 'decrease',
    })
  }

  // Handle mileage adjustments (only one should be active)
  const mileageAdj = toNumber(result.AdjustMileage)
  const highMileageAdj = toNumber(result.AdjustHighMileage)

  if (mileageAdj !== 0) {
    adjustments.push({
      label: mileageAdj < 0 ? adj('lowMileage') : adj('highMileage'),
      value: mileageAdj,
      description: mileageAdj < 0 ? adj('lowMileageDescription') : adj('highMileageDescription'),
      type: mileageAdj > 0 ? 'increase' : 'decrease',
    })
  }

  if (highMileageAdj !== 0 && mileageAdj === 0) {
    adjustments.push({
      label: adj('highMileage'),
      value: highMileageAdj,
      description: adj('highMileageDescription'),
      type: highMileageAdj > 0 ? 'increase' : 'decrease',
    })
  }

  if (toNumber(result.AdjustManualCorrection) !== 0) {
    adjustments.push({
      label: adj('manualCorrection'),
      value: toNumber(result.AdjustManualCorrection),
      description: result.CommentManualCorrection || adj('manualCorrectionDescription'),
      type: toNumber(result.AdjustManualCorrection) > 0 ? 'increase' : 'decrease',
    })
  }

  return adjustments
}
